<template>
  <div
    :class="[
      $style.root,
      isHidden && $style.rootHidden,
      isSuccess && $style.rootSuccess,
    ]"
    @click="onClick"
  >
    <span :class="$style.count" :style="{ transform: `scale(${countScale})` }">
      X{{ clickedCount }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';

const CLICK_DURATION_MS = 1000;

interface Props {
  count: number;
  showAfterClicks?: number;
}

const props = withDefaults(defineProps<Props>(), {
  showAfterClicks: 1,
});

const emit = defineEmits<{
  click: [count: number];
  start: [];
  success: [];
}>();

const clickedCount = ref(0);
const isHidden = computed(() => clickedCount.value < props.showAfterClicks);
const isSuccess = computed(() => clickedCount.value === props.count);
const countScale = computed(() => 0.1 * clickedCount.value + 1);

const activeTimers = new Set<ReturnType<typeof setTimeout>>();
let resetTimerId: ReturnType<typeof setTimeout> | null = null;
let internalClickCount = 0;

function trackedTimeout(
  callback: () => void,
  delay: number
): ReturnType<typeof setTimeout> {
  const id = setTimeout(() => {
    activeTimers.delete(id);
    callback();
  }, delay);
  activeTimers.add(id);
  return id;
}

function setClicked(value: number): void {
  clickedCount.value = value;
  emit('click', value);
}

function onClick(): void {
  internalClickCount++;
  setClicked(Math.min(internalClickCount, props.count));

  if (internalClickCount === 1) {
    emit('start');
    resetTimerId = trackedTimeout(() => {
      internalClickCount = 0;
      setClicked(0);
    }, props.count * CLICK_DURATION_MS);
  }

  if (internalClickCount === props.count) {
    trackedTimeout(() => {
      if (resetTimerId !== null) {
        clearTimeout(resetTimerId);
        activeTimers.delete(resetTimerId);
        resetTimerId = null;
      }
      internalClickCount = 0;
      setClicked(0);
      emit('success');
    }, CLICK_DURATION_MS);
  }
}

onBeforeUnmount(() => {
  for (const id of activeTimers) clearTimeout(id);
  activeTimers.clear();
});
</script>

<style module>
.root {
  user-select: none;
}

.rootHidden .count {
  transition: none;
  opacity: 0;
}

.rootSuccess .count {
  color: var(--Global--success-color);
  text-shadow: 0 0 0.2em var(--Global--success-color);
}

.count {
  letter-spacing: 0.11rem;
  font-size: 200%;
  text-shadow: 0 0 0.2em var(--Kiosk--font_color);
  transition: opacity 100ms;
}

.count::first-letter {
  font-size: 75%;
}
</style>
